<script>
import {useRoute, useRouter} from "vue-router";
import {useUserStore} from "../../../stores/user";
import {useDonationStore} from "../../../stores/donation";
import {ApplicationHelper} from "../../../helpers/ApplicationHelper";
import DonationCard from "../components/DonationCard.vue";
import Drawer from "../../components/Drawer.vue";
import {useSubscriptionStore} from "../../../stores/subscription";
import Icon from "../../components/Icon.vue";
import Tooltip from "../../components/tooltip.vue";
import Popup from "../../components/popup.vue";
import {useLandingPageStore} from "@/javascript/stores/landingPage";
import DataCard from "../../components/overview/DataCard.vue";
import Paginator from "../../components/Pagination.vue";

export default {
  name: "DonationManagement",
  components: {Paginator, Popup, Tooltip, Icon, Drawer, DonationCard, DataCard},
  props: {
    /**
     * When this is the gifts table, when false this is the summary table in the overview
     */
    paginate: {
      type: Boolean,
      default: false
    },
  },
  setup() {
    const router = useRouter();
    const route = useRoute();

    const userStore = useUserStore();
    const donationStore = useDonationStore();
    const subscriptionStore = useSubscriptionStore();
    const landingPageStore = useLandingPageStore();

    return {
      route,
      router,
      userStore,
      donationStore,
      subscriptionStore,
      landingPageStore,
    };
  },
  data() {
    return {
      activeDonation: {},
      popupMessage: "",
    };
  },
  computed: {
    ApplicationHelper() {
      return ApplicationHelper;
    },
    donations() {
      if(this.paginate) {
        return this.userStore.donations || [];
      } else {
        return this.userStore.donationSnapshot || [];
      }
    },
    loaded() {
      return !!this.userStore.user;
    },
    additionalText() {
      return this.paginate
        ? "Your full gift history."
        : `A history of your latest contributions.`;
    },
  },

  mounted() {
    // Open drawer if :id present
    const id = this.route.params.id;
    if (id) {
      const donation = this.donations.find(d => d.id?.toString() === id || d.uuid === id);
      if (donation) {
        this.openDrawer(donation);
      } else {
        this.closeDrawer();
      }
    }
  },


  methods: {
    openDrawer(donation) {
      this.router.replace({
        params: { ...this.route.params, id: donation.uuid }
      });
      this.activeDonation = donation;
      this.$refs.drawer.show();
    },

    closeDrawer() {
      this.activeDonation = {};
      this.router.replace({
        params: { ...this.route.params, id: '' }
      });
    },

    onDonationClick(donation) {
      this.openDrawer(donation);
    },

    resendReceipt() {
      this.donationStore.resendDonationReceipt(this.activeDonation).then((res) => {
        this.showPopup(res ? 'Receipt has been sent to your email!' : 'Something went wrong! Please try again later.');
      });
    },
    showPopup(message) {
      this.popupMessage = message;
      this.$refs.popup.show();
    }
  }
};
</script>

<template>
  <data-card
    :loaded="loaded"
    title="Gifts"
    title-class="text-lg font-semibold"
    :additional-text="additionalText"
    additional-text-class="text-xs text-gray-500 font-normal"
    :left-sidebar="false"
    bordered
    border-class="border border-slate-200 rounded-lg shadow"
  >
    <div>
      <ul class="flex flex-col gap-2">
        <li
          v-for="(donation, idx) in donations"
          :key="donation.id || donation.uuid"
          class="w-full"
        >
          <donation-card
            :donation="donation"
            :alt-row="idx % 2 === 1"
            clickable
            @click="onDonationClick(donation)"
          />
        </li>
      </ul>

      <div
        v-if="paginate"
        class="mt-3"
      >
        <paginator
          class="pagination"
          :page="userStore.userDonationsMeta.page"
          :total-pages="userStore.userDonationsMeta.pages"
          :records-showing="userStore.donations.length"
          :records-per-page="userStore.userDonationsMeta.limit"
          :total-records="userStore.userDonationsMeta.count"
          @next="userStore.nextPage"
          @prev="userStore.prevPage"
          @move-to-page="userStore.moveToPage"
        />
      </div>
    </div>
  </data-card>

  <teleport to="body">
    <drawer
      ref="drawer"
      size="large"
      @hide="closeDrawer"
    >
      <div class="donation-details-drawer">
        <div class="donation-amount-header">
          <div class="flex flex-col items-center">
            <div class="text-xs font-bold uppercase">
              Your gift to {{ activeDonation.organization_name }}
            </div>
            <div class="m-2">
              {{ ApplicationHelper.formatMoney(activeDonation.amount) }}
            </div>
            <div class="text-sm">
              {{ activeDonation.organization_currency || "USD" }}
            </div>
          </div>
        </div>

        <div class="donation-details-table">
          <div class="font-semibold mt-4 mb-2">
            Gift Details
          </div>
          <table>
            <tr><td>Amount</td><td>{{ ApplicationHelper.formatMoney(activeDonation.amount) }}</td></tr>
            <tr><td>Designation</td><td>{{ activeDonation.donatable?.name || 'N/A' }}</td></tr>
            <tr class="divider" />
            <tr>
              <td>Payment Method</td>
              <td>
                <img
                  v-if="activeDonation.processor_type?.includes('Stripe')"
                  src="@/assets/logos/stripe.svg"
                  style="height: 24px;"
                  alt="Stripe Logo"
                >
                <img
                  v-else-if="activeDonation.processor_type?.includes('Helcim')"
                  src="@/assets/logos/helcim_logo.svg"
                  style="height: 24px;"
                  alt="Helcim Logo"
                >
              </td>
            </tr>
            <tr>
              <td>Status</td><td class="capitalize">
                {{ activeDonation.status }}
              </td>
            </tr>
            <tr v-if="activeDonation.fee_amount > 0">
              <td>Platform Contribution</td><td>{{ ApplicationHelper.formatMoney(activeDonation.fee_amount, true) }}</td>
            </tr>
            <tr>
              <td>Date Paid</td>
              <td>{{ activeDonation.paid_at ? ApplicationHelper.formatDate(activeDonation.paid_at, {weekday:false, time:false}) : 'N/A' }}</td>
            </tr>
            <tr v-if="activeDonation.subscription">
              <td>Recurs</td>
              <td>
                <span>{{ subscriptionStore.intervalString(activeDonation.subscription) }}</span>
              </td>
            </tr>
          </table>
        </div>

        <div class=" m-4 border border-slate-200" />
        <div>
          <div
            class="resend-receipt-btn"
            @click="resendReceipt"
          >
            <div class="content">
              <div class="flex flex-row content-center items-center">
                <icon
                  icon="invoice"
                  size="24px"
                  color="#4F4F4F"
                />
                <div class="ml-2 body-1-medium">
                  Resend receipt
                </div>
              </div>
              <icon icon="chevron-right" />
            </div>
          </div>
        </div>
      </div>
    </drawer>
  </teleport>

  <popup
    ref="popup"
    :message="popupMessage"
    :duration="5000"
  />
</template>

<style scoped lang="scss">
.donation-details-drawer {
  @apply mt-16 m-6;

  .donation-amount-header {
    @apply p-12 rounded-md;
    @apply flex flex-col items-center justify-center text-center;
    @apply text-3xl font-semibold;
  }

  .donation-details-table {
    @apply w-full overflow-x-auto;
    table {
      @apply w-full;
      tr {
        @apply flex flex-col sm:flex-row justify-between mt-2 gap-2;

        td { @apply break-words; }
        td:nth-child(1) { @apply font-semibold w-full sm:w-auto; }
        td:nth-child(2) { @apply w-full sm:flex-1; }

        &.divider { @apply m-4 border border-slate-200; }
      }
    }
  }

  .resend-receipt-btn {
    @apply rounded-md cursor-pointer;
    background-color: rgba(16, 190, 90, 0.1);
    position: relative;

    .content { @apply w-full flex flex-row justify-between gap-3 rounded-md items-center; @apply relative z-10; }

    &::after {
      content: "";
      position: absolute;
      @apply absolute top-0 left-0 right-0 bottom-0 opacity-0;
      @apply rounded-md cursor-pointer;
      background: linear-gradient(123deg, rgba(16, 190, 90, 0.2) 52%, rgba(0, 189, 255, 0.17) 100%);
      transition: opacity 0.5s ease-in-out;
      z-index: 1;
      pointer-events: none;
    }

    &:hover::after { opacity: 1; }
  }
}
</style>
